import { createCodePlugin } from "@streamdown/code";
import { type ComponentProps, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { defaultRemarkPlugins, Streamdown, type StreamdownTranslations } from "streamdown";
import { MarkdownImage, MarkdownLink } from "./MarkdownMedia";
import { prepareMarkdown } from "./markdownContent";

type StreamdownProps = ComponentProps<typeof Streamdown>;
export type MarkdownRemarkPlugins = NonNullable<StreamdownProps["remarkPlugins"]>;
export type MarkdownComponents = NonNullable<StreamdownProps["components"]>;

// High-contrast GitHub themes: the regular ones miss WCAG AA on some tokens.
const SHIKI_THEME: ["github-light-high-contrast", "github-dark-high-contrast"] = [
  "github-light-high-contrast",
  "github-dark-high-contrast",
];
const PLUGINS = { code: createCodePlugin({ themes: SHIKI_THEME }) };
const LINK_SAFETY = { enabled: false } as const;
const COMPONENTS = { a: MarkdownLink, img: MarkdownImage };

function useMarkdownTranslations(): StreamdownTranslations {
  const { t } = useTranslation("chat", { keyPrefix: "markdown" });
  return {
    close: t("close"),
    copied: t("copied"),
    copyCode: t("copyCode"),
    copyLink: t("copyLink"),
    copyTable: t("copyTable"),
    copyTableAsCsv: t("copyTableAsCsv"),
    copyTableAsMarkdown: t("copyTableAsMarkdown"),
    copyTableAsTsv: t("copyTableAsTsv"),
    downloadDiagram: t("downloadDiagram"),
    downloadDiagramAsMmd: t("downloadDiagramAsMmd"),
    downloadDiagramAsPng: t("downloadDiagramAsPng"),
    downloadDiagramAsSvg: t("downloadDiagramAsSvg"),
    downloadFile: t("downloadFile"),
    downloadImage: t("downloadImage"),
    downloadTable: t("downloadTable"),
    downloadTableAsCsv: t("downloadTableAsCsv"),
    downloadTableAsMarkdown: t("downloadTableAsMarkdown"),
    exitFullscreen: t("exitFullscreen"),
    externalLinkWarning: t("externalLinkWarning"),
    imageNotAvailable: t("imageNotAvailable"),
    mermaidFormatMmd: t("mermaidFormatMmd"),
    mermaidFormatPng: t("mermaidFormatPng"),
    mermaidFormatSvg: t("mermaidFormatSvg"),
    openExternalLink: t("openExternalLink"),
    openLink: t("openLink"),
    tableFormatCsv: t("tableFormatCsv"),
    tableFormatMarkdown: t("tableFormatMarkdown"),
    tableFormatTsv: t("tableFormatTsv"),
    viewFullscreen: t("viewFullscreen"),
  };
}

/**
 * Assistant output as rendered markdown: GFM (tables, task lists, links),
 * highlighted code with copy, and tolerant of half-received markdown while
 * `streaming` (unclosed fences/emphasis render cleanly instead of flickering).
 * Links and images pointing at the backend (`/chat/attachments/...`) are fetched with the user's token.
 *
 * Other surfaces can extend it: `remarkPlugins` run after the defaults (GFM etc.
 * stay on), and `components` override individual elements (e.g. `a`).
 */
export function Markdown({
  children,
  streaming = false,
  remarkPlugins,
  components,
}: Readonly<{
  children: string;
  streaming?: boolean;
  remarkPlugins?: MarkdownRemarkPlugins;
  components?: MarkdownComponents;
}>) {
  const translations = useMarkdownTranslations();
  const content = useMemo(() => prepareMarkdown(children, streaming), [children, streaming]);
  const plugins = useMemo(
    () => (remarkPlugins ? [...Object.values(defaultRemarkPlugins), ...remarkPlugins] : undefined),
    [remarkPlugins],
  );
  const merged = useMemo(() => (components ? { ...COMPONENTS, ...components } : COMPONENTS), [components]);
  return (
    <Streamdown
      mode={streaming ? "streaming" : "static"}
      isAnimating={streaming}
      plugins={PLUGINS}
      shikiTheme={SHIKI_THEME}
      linkSafety={LINK_SAFETY}
      components={merged}
      remarkPlugins={plugins}
      translations={translations}
      className="chat-markdown text-sm leading-relaxed text-content-primary"
    >
      {content}
    </Streamdown>
  );
}
